/**
 * DeliveryTypeField — dropdown de tipo de entrega + campo extra (mensagem/link)
 * quando o tipo escolhido precisa de um valor adicional.
 *
 * Fonte ÚNICA da lógica de "trocar tipo → montar o objeto de entrega certo"
 * (preserva mensagem/link ao alternar de volta pro mesmo tipo, zera pros
 * demais) e da renderização condicional dos campos extras. Usado em todo
 * lugar que edita uma entrega — Entrega Padrão do bot, Entrega Global de
 * Upsell/Downsell, Entrega deste Upsell/Downsell, Entrega deste Plano — pra
 * não duplicar esse if/else de novo.
 *
 * Props:
 * - value: objeto de entrega ({type, message?, url?}) ou null/undefined (= 'default')
 * - onChange: (delivery|null) => void
 * - options: [{ value, label, icon, colorClass }] (ver DeliveryTypeSelect)
 * - showReset?: mostra botão "voltar ao padrão" ao lado do select (telas de
 *   entrega padrão/global); forms de oferta/plano deixam false (o próprio
 *   select já tem a opção "default"/"usar padrão" na lista).
 * - panel?: true = campo extra com label + espaçamento de card (telas de
 *   entrega padrão/global); false = inline compacto (forms de oferta/plano).
 */

import { RotateCcw } from 'lucide-react';
import DeliveryTypeSelect from './DeliveryTypeSelect';

export default function DeliveryTypeField({
    value,
    onChange,
    options,
    showReset = false,
    resetTitle = 'Voltar ao padrão',
    panel = false,
    messagePlaceholder = 'Obrigado pela compra! Aqui está seu acesso...',
    linkPlaceholder = 'https://...',
    className = '',
}) {
    const type = value?.type || 'default';
    // Entrega já salva que hoje é bloqueada neste contexto (ex.: "Apenas
    // Upsell" num bot Global/com cartão): o backend recusa salvar assim.
    const blockedSelection = options.find((o) => o.value === type && o.disabled);

    const setType = (t) => {
        if (t === 'message_only') return onChange({ type: t, message: value?.message || '' });
        if (t === 'external_link') return onChange({ type: t, url: value?.url || '' });
        if (t === 'default') return onChange(null);
        return onChange({ type: t }); // vip_group, only_upsell (e futuros tipos sem campo extra)
    };

    const extraField = (label, node) => panel ? (
        <div className="mt-3">
            <label className="text-xs text-white/40 mb-1 block">{label}</label>
            {node}
        </div>
    ) : <div className="mt-2">{node}</div>;

    return (
        <>
            <div className={`flex gap-2 ${className}`}>
                <DeliveryTypeSelect className="flex-1" value={type} onChange={setType} options={options} />
                {showReset && type !== 'default' && (
                    <button
                        type="button"
                        title={resetTitle}
                        onClick={() => onChange(null)}
                        className="w-9 h-9 shrink-0 rounded-lg border border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white hover:border-white/[0.15] transition-colors flex items-center justify-center"
                    >
                        <RotateCcw size={14} />
                    </button>
                )}
            </div>

            {blockedSelection && (
                <p role="alert" className="mt-2 text-xs text-amber-300/90">
                    "{blockedSelection.label}" não está disponível aqui ({(blockedSelection.hint || '').toLowerCase()}).
                    Escolha outra entrega para salvar.
                </p>
            )}

            {type === 'message_only' && extraField('Mensagem de Entrega',
                <textarea
                    className="input3 w-full text-sm"
                    rows={panel ? 2 : 3}
                    placeholder={messagePlaceholder}
                    value={value?.message || ''}
                    onChange={(e) => onChange({ ...value, type, message: e.target.value })}
                />
            )}

            {type === 'external_link' && extraField('Link de Entrega',
                <input
                    className="input3 w-full text-sm"
                    placeholder={linkPlaceholder}
                    value={value?.url || ''}
                    onChange={(e) => onChange({ ...value, type, url: e.target.value })}
                />
            )}
        </>
    );
}
